import { useEffect, useLayoutEffect, useState } from 'react';
import { Breakpoint } from '../const';

/**
 * Returns true if the viewport is mobile size
 * Returns undefined until the first render occured to prevent hydration errors.
 */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${Breakpoint.Md - 0.02}px)`);
}

/**
 * A custom React hook to determine if the current device size/resolution is desktop-like (LG+).
 *
 * This hook utilizes media queries to assess the viewport width. Initially, it returns `undefined`
 * to prevent hydration errors during server-side rendering, and after the first render, it returns
 * a boolean indicating whether the current viewport meets the desktop criteria.
 *
 * @returns {undefined} - until the first render to prevent hydration errors.
 * @returns {boolean} - `true` if the viewport width is greater than or equal to the LG breakpoint, otherwise returns `false`.
 *
 * @example - usage:
 *   const isDesktop = useIsDesktop();
 *
 *   return isDesktop ? <desktop-specific stuff> : <content not meant for desktops>;
 */
export const useIsDesktop = (): boolean | undefined => {
  return useMediaQuery(`(min-width: ${Breakpoint.Lg}px)`);
};

const getMatches = (query: string): boolean => {
  // Prevents SSR issues
  if (typeof window !== 'undefined') {
    return window.matchMedia(query).matches;
  }
  return false;
};

/**
 * Returns true if the given media query matches.
 * Returns undefined until the first render occured to prevent hydration errors.
 * @param query A media query as string
 * @returns
 */
export function useMediaQuery(query: string): boolean | undefined {
  const [hasMatch, setHasMatches] = useState<boolean>(getMatches(query));
  const [initialLoad, setInitialLoad] = useState(true);
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      handleChange();
    }
  }, []);

  function handleChange() {
    setHasMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query);
    const isModern = !!matchMedia?.addEventListener;
    handleChange();
    if (isModern) {
      matchMedia.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      matchMedia.addListener(handleChange);
    }
    return () => {
      if (isModern) {
        matchMedia.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        matchMedia.removeListener(handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return initialLoad ? undefined : hasMatch;
}

/**
 * Counts how many of the given media queries apply
 * Returns undefined until the first render occured to prevent hydration errors.
 * @param queries An array of media query strings
 * @returns An array of booleans indicating if the media queries match
 */
export function useMediaQueries(queries: string[]): boolean[] | undefined[] {
  const [hasMatch, setHasMatch] = useState<boolean[]>([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      handleChange();
    }
  }, []);

  function handleChange() {
    const results = [...queries];
    setHasMatch(results.map(query => !!getMatches(query)));
  }

  useEffect(() => {
    const matchMedias: MediaQueryList[] = [];
    queries.forEach(query => {
      matchMedias.push(window.matchMedia(query));
    });
    const isModern = !!matchMedias[0].addEventListener;
    matchMedias.forEach(matchMedia => {
      if (isModern) {
        matchMedia.addEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        matchMedia.addListener(handleChange);
      }
    });
    return () => {
      matchMedias.forEach(matchMedia => {
        if (isModern) {
          matchMedia.removeEventListener('change', handleChange);
        } else {
          // Fallback for older browsers
          matchMedia.removeListener(handleChange);
        }
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  return initialLoad ? queries.map(() => undefined) : hasMatch;
}
